import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Container, Grid, CircularProgress, Typography, Button, Box } from '@mui/material';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { withStyles } from '@mui/styles';
import { useHistory } from 'react-router-dom';
import { FormProvider, useFieldArray, useForm, Controller } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import _ from 'lodash';
import FlagIcon from '@mui/icons-material/Flag';
import moment from 'moment';
import { Alert as MuiAlert } from '@mui/lab';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ExpenseButtons from '../Components/Expense/ExpenseButtons';
import ExpenseReceiptUploadForm from '../Components/Expense/ExpenseReceiptUploadForm';
import ExpenseEntryHeader from '../Components/Expense/ExpenseEntryHeader';
import {
    createExpense,
    updateExpense,
    getExpenseById,
    getExpenseCategories,
    getStatusesAllowed,
} from '../Services/ExpenseService';
import { getDivisions } from '../Services/Common';
import { formatErrorMessage } from '../Utils/ErrorMessage';
import { uploadImages } from '../Utils/UploadImages';
import Alert from '../Components/Alert';
import ConfirmationModal from '../Components/Dialog/ConfirmationModal';
import ExpenseTabs from '../Components/Expense/Tabs/ExpenseTabs';
import ExpenseTab from '../Components/Expense/Tabs/ExpenseTab';
import ExpenseTabLabel from '../Components/Expense/Tabs/ExpenseTabLabel';
import AddExpenseTab from '../Components/Expense/Tabs/AddExpenseTab';
import ExpenseTabPanel from '../Components/Expense/Tabs/ExpenseTabPanel';
import BookingDetailsForm from '../Components/Expense/BookingDetailsForm';
import ClinicianExpenseDetailsForm from '../Components/Expense/ClinicianExpenseDetailsForm';
import ExpenseSummary from '../Components/Expense/ExpenseSummary';
import InternalNotesForm from '../Components/Expense/InternalNotesForm';
import ReasonForSplittingForm from '../Components/Expense/ReasonForSplittingForm';
import {
    ExpenseStatusEnum,
    ExpenseStatusValuesEnum,
} from '../Components/Expense/ExpenseStatus.enum';
import { ExpenseClassEnum } from '../Components/Expense/ExpenseClass.enum';
import { BillTypeEnum } from '../Components/Expense/BillType.enum';
import ExpenseNeedsReviewAlert from '../Components/Expense/ExpenseNeedsReviewAlert';
import { COLORS } from '../../utils/Application_Constants';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';
import { currencies } from '../Configurations/ExpenseEntryConfig';
import {
    createCorrectionExpenseFromEntries,
    createExpenseFromExpenseEntries,
    isExpenseViewOnlyByStatus,
    shouldEnableByCorrectionAndApprovedStatus,
    validateAllPostingDatesOnSubmit,
} from '../Utils/ExpenseUtil';
import { ExpenseModeEnum } from '../Components/Expense/ExpenseMode.enum';
import eventBus from '../Components/Expense/EventBus';

const styles = (theme) => ({
    ContainerRoot: {
        [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
        paddingTop: '1.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    formGridContainer: {
        gap: 32,
    },
    summary: {
        position: 'sticky',
        top: '5rem',
        paddingTop: '.5rem',
    },
    warningMessage: {
        color: COLORS.LT_SUNFLOWER,
    },
    iconContainer: {
        position: 'absolute',
        top: 8,
        right: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    flagIcon: {
        cursor: 'pointer',
    },
    viewBookingButton: {
        minWidth: 'auto',
        padding: '4px 8px',
        fontSize: '0.75rem',
    },
    tabPanel: {
        position: 'relative',
    },
    alertContainer: {
        marginTop: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(4),
        },
        paddingLeft: '.5rem',
    },
    leftIconContainer: {
        position: 'absolute',
        top: 8,
        left: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    editButton: {
        backgroundColor: COLORS.LT_JOURNEY10,
        '&:hover': {
            backgroundColor: COLORS.LT_JOURNEY25,
        },
        color: COLORS.LT_JOURNEY,
    },
});

const defaultExpense = {
    bookingId: null,
    clinicianId: '',
    clinicianFirstName: '',
    clinicianLastName: '',
    clientId: '',
    division: '',
    state: '',
    billType: '',
    category: '',
    type: '',
    currency: 'USD',
    amount: '',
    reimbursedAmount: '',
    accountingProcessingNotes: '',
    label: 'Expense',
    mileage: '',
    status: ExpenseStatusValuesEnum.New,
    salespersonName: '',
    recruiterName: '',
    payDate: null,
    expenseClass: ExpenseClassEnum.Reimbursement,
    splitTransactionComment: '',
    source: 'LtAssociate',
    clinicianFacingComments: '',
    commentsFromClinician: '',
    merchantName: '',
    highPriority: false,
};

const ClinicianReimbursement = (props) => {
    const { classes } = props;
    const flags = useFlags();
    const {
        ltoeWebExpensesEnableEditButtonClinicianReimbursement: enableEditButtonByFF,
        ltoeWebExpensesEnablePostDateClinicianReimbursement: enablePostDate,
    } = flags;
    const [isLoading, setIsLoading] = useState(false);
    const { isExpenseSystemAdministratorUser, isExpenseAdminUser } = useContext(AdminAuthContext);
    const isViewOnly = !(isExpenseSystemAdministratorUser || isExpenseAdminUser);
    const [expenseCategories, setExpenseCategories] = useState(null);
    const [divisions, setDivisions] = useState([]);
    const [statusesAllowed, setStatusesAllowed] = useState([]);
    const { id } = useParams();
    const history = useHistory();
    const [alert, setAlert] = useState();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [removeTabs, setRemoveTabs] = useState(false);
    const backLink = id ? 'All Expenses' : 'Expense Types';
    const title = id ? 'Clinician Reimbursement' : 'Clinician Reimbursement Expense Entry';
    const expensesPath = id ? '/expenses/review' : '/expenses';
    const [originalExpenseEntries, setOriginalExpenseEntries] = useState([]);
    const [originalPrimaryExpense, setOriginalPrimaryExpense] = useState();
    const [ocrUsageLogs, setOcrUsageLogs] = useState([]);
    const [isCorrection, setIsCorrection] = useState(false);
    const [ocrUsedValues, setUsedOcrValues] = useState([]);
    const [isAnyExpensePosted, setIsAnyExpensePosted] = useState(false);
    const defaultValues = {
        fileUploads: [{ newFiles: [], existingFiles: [] }],
        expenseEntries: [defaultExpense],
    };
    const methods = useForm({ shouldUnregister: false, defaultValues });
    const {
        handleSubmit,
        reset,
        control,
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
        unregister,
        trigger,
    } = methods;
    const {
        fields: expenseEntries,
        append,
        remove,
    } = useFieldArray({
        control, // control props comes from useForm (optional: if you are using FormContext)
        name: 'expenseEntries', // unique name for your Field Array
        keyName: 'expenseEntryId',
    });
    const watchExpenseEntries = watch(`expenseEntries`);
    const isAnyApproved = Object.keys(watchExpenseEntries).some(
        (k) => watchExpenseEntries[k].status === ExpenseStatusValuesEnum.Approved
    );
    const [showBalanceError, setShowBalanceError] = useState(false);
    const [showBalanceWarning, setShowBalanceWarning] = useState(false);
    const hasErrors = !!Object.keys(methods.formState.errors).length;
    const [assignmentsByIndex, setAssignmentsByIndex] = useState({});
    const [openConfirmationEditModal, setOpenConfirmationEditModal] = useState(false);
    const [enableEditByModal, setEnableEditByModal] = useState(false);
    const [enableEditByCorrection, setEnableEditByCorrection] = useState(false);

    const handleAssignmentChange = (index, assignment) => {
        setAssignmentsByIndex((prev) => ({
            ...prev,
            [index]: assignment,
        }));
    };

    useEffect(() => {
        Promise.all([
            getDivisions(),
            getStatusesAllowed(ExpenseClassEnum.Reimbursement),
            getExpenseCategories(ExpenseClassEnum.Reimbursement),
        ])
            .then(async ([divs, statuses, cats]) => {
                setExpenseCategories(cats);
                setDivisions(divs);
                setStatusesAllowed(statuses);
            })
            .catch((error) => {
                console.error(error);
            });

        if (id) {
            fetchExpenseById();
        }
    }, []);

    useEffect(() => {
        if (watchExpenseEntries.length > 1) {
            const firstExpenseComment = watchExpenseEntries[0]?.commentsFromClinician || '';

            watchExpenseEntries.forEach((expense, idx) => {
                if (idx !== 0 && expense.commentsFromClinician !== firstExpenseComment) {
                    setValue(`expenseEntries[${idx}].commentsFromClinician`, firstExpenseComment);
                }
            });
        }
    }, [watchExpenseEntries, setValue]);

    useEffect(() => {
        const currentExpense = watch(`expenseEntries.${activeTabIndex}`);
        if (currentExpense) {
            setEnableEditByCorrection(shouldEnableByCorrectionAndApprovedStatus(currentExpense));
        }
    }, [watchExpenseEntries, activeTabIndex]);

    // This was added to address a rendering issue
    // It turns off the TabPanel Display removes the item and then turns back on the display
    useEffect(() => {
        if (removeTabs) {
            const idx = activeTabIndex;
            resetImages(idx);
            remove(idx);
            unregister([`expenseEntries.${idx}`, `fileUploads.${idx}`]);
            clearErrors(['expenseEntries', 'fileUploads']);
            const current = {
                expenseEntries: getValues('expenseEntries'),
                fileUploads: getValues('fileUploads'),
            };
            reset(current);
            setActiveTabIndex((prev) => Math.max(prev - 1, 0));
            setRemoveTabs(false);
        }
    }, [removeTabs]);

    useEffect(() => {
        setEnableEditByModal(false);
    }, [activeTabIndex]);

    const fetchExpenseById = async () => {
        try {
            setIsLoading(true);
            const res = await getExpenseById(id);
            const listExpenses = [];
            const aOriginalExpenseEntries = [];
            const aFileUploads = [];
            if (res.primaryExpenseId && res.primaryExpense) {
                // child split
                let childIndex = 0;
                listExpenses.push(res.primaryExpense);
                for (let i = 0; i < res.primaryExpense.splitExpenses.length; i++) {
                    const listExpensesIndex = listExpenses.push(
                        res.primaryExpense.splitExpenses[i]
                    );
                    if (res.primaryExpense.splitExpenses[i].id === id) {
                        childIndex = listExpensesIndex - 1;
                    }
                }
                setActiveTabIndex(childIndex);
            } else {
                listExpenses.push(res);
                for (let i = 0; i < res.splitExpenses.length; i++) {
                    listExpenses.push(res.splitExpenses[i]);
                }
            }

            listExpenses[0].splitExpenses = [];

            for (let i = 0; i < listExpenses.length; i++) {
                const formattedValues = formatResponse(listExpenses[i]);
                // Reset back to Null as empty string on the as the default was causing issues
                formattedValues.payDate =
                    _.isString(formattedValues.payDate) && formattedValues.payDate.length === 0
                        ? null
                        : formattedValues.payDate.substring(0, 10);
                aOriginalExpenseEntries.push({
                    ...formattedValues,
                    label: listExpenses[i].label ? listExpenses[i].label : `Expense ${i}`,
                });
                if (
                    listExpenses[i] &&
                    listExpenses[i].expenseImages &&
                    listExpenses[i].expenseImages.length >= 0
                ) {
                    aFileUploads.push({
                        newFiles: [],
                        existingFiles: listExpenses[i].expenseImages,
                    });
                } else {
                    aFileUploads.push({ newFiles: [], existingFiles: [] });
                }

                if (!isCorrection && listExpenses[i].expenseVerion > 0) {
                    setIsCorrection(true);
                }
            }
            setOriginalExpenseEntries(aOriginalExpenseEntries);
            setOriginalPrimaryExpense(aOriginalExpenseEntries[0]);
            IsAnyExpensePostedCheck(aOriginalExpenseEntries[0]);
            const defaultValues = {
                fileUploads: aFileUploads,
                expenseEntries: aOriginalExpenseEntries,
            };
            reset(defaultValues);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    const IsAnyExpensePostedCheck = () => {
        if (originalExpenseEntries?.length > 0) {
            setIsAnyExpensePosted(
                originalExpenseEntries.some(
                    (entry) => entry.status === ExpenseStatusValuesEnum.Posted
                )
            );
        }
    };

    const formatResponse = (res) =>
        Object.fromEntries(
            Object.entries(res).map(([key, value]) => [key, value === null ? '' : value])
        );

    const clearAllHiddenErrors = () => {
        const aExpenseEntries = getValues('expenseEntries');
        aExpenseEntries.map((element, index) => {
            clearExpenseHiddenErrors(element, index);
        });
    };

    const clearExpenseHiddenErrors = (expense, index) => {
        // Field 1: Transaction Date  transactionDate
        if (!expense.category) {
            clearErrors(`expenseEntries[${index}].transactionDate`);
        }

        // Field 2: Start Date  startDate
        // Field 3: End Date  endDate
        if (
            expense.expenseClass === 3 ||
            expense.expenseClass === ExpenseClassEnum.SalesGeneralAndAdmin
        ) {
            if (!(expense.category === 'Other' && expense.type === 'LodgingSecurityDeposit')) {
                clearErrors(`expenseEntries[${index}].startDate`);
                clearErrors(`expenseEntries[${index}].endDate`);
            }
        } else if (
            !(
                expense.category === 'Travel' &&
                !(expense.type === 'Baggage' || expense.type === 'Fuel' || !expense.type)
            )
        ) {
            clearErrors(`expenseEntries[${index}].startDate`);
            clearErrors(`expenseEntries[${index}].endDate`);
        }

        // Field 4: Delivery Date  deliveryDate
        // if (expense.status !== ExpenseStatusValuesEnum.Approved) {
        //     clearErrors(`expenseEntries[${index}].deliveryDate`);
        // }

        // Field 5: Pay Date(Credit Card Transaction)  payDate
        if (
            !(
                expense.expenseClass === ExpenseClassEnum.Reimbursement ||
                (expense.billType === BillTypeEnum.ClinicianDeduction &&
                    expense.expenseClass === ExpenseClassEnum.IngestedCCTransaction)
            )
        ) {
            clearErrors(`expenseEntries[${index}].payDate`);
        }

        // Field 6: Mileage  mileage
        if (!(expense.category === 'Travel' && expense.type === 'Mileage')) {
            clearErrors(`expenseEntries[${index}].mileage`);
        }

        // Field 7:  On Hold Reason
        if (expense.status !== ExpenseStatusValuesEnum.OnHoldReimbursement) {
            clearErrors(`expenseEntries[${index}].onHoldComment`);
        }

        // Field 8 : Misc Description
        if (expense.category !== 'Misc' || expense.type !== 'Misc') {
            clearErrors(`expenseEntries[${index}].miscDescription`);
        }
    };

    const autoFillWithOCR = (ocrData) => {
        const confidenceThreshold = 0.7;
        const expenseVals = getValues(`expenseEntries[${activeTabIndex}]`);

        let usedTransactionDate = false;
        let usedMerchantName = false;
        let usedAmount = false;
        let usedCurrency = false;

        if (
            !expenseVals.transactionDate &&
            !!ocrData.transactionDate?.date &&
            ocrData.transactionDate?.confidence >= confidenceThreshold
        ) {
            // Without this, the transaction date is off by one day visually (still saves correctly)
            const dateOffByOneCorrected = moment
                .utc(ocrData.transactionDate?.date)
                .format('YYYY-MM-DD');
            expenseVals.transactionDate = dateOffByOneCorrected;
            eventBus.emit(`txnDateChanged${activeTabIndex}`, {
                data: dateOffByOneCorrected,
            });
            usedTransactionDate = true;

            const ocrVal = {
                scanId: ocrData.scanId,
                transactionDate: ocrData.transactionDate?.date,
            };

            setUsedOcrValues((ocrUsedValues) => [...ocrUsedValues, ocrVal]);
        }

        if (
            !expenseVals.merchantName &&
            !!ocrData.merchantName?.name &&
            ocrData.merchantName?.confidence >= confidenceThreshold
        ) {
            expenseVals.merchantName = ocrData.merchantName?.name;
            usedMerchantName = true;

            const ocrVal = {
                scanId: ocrData.scanId,
                merchantName: ocrData.merchantName?.name,
            };

            setUsedOcrValues((ocrUsedValues) => [...ocrUsedValues, ocrVal]);
        }

        if (
            !expenseVals.amount &&
            !!ocrData.expenseAmount?.amount &&
            ocrData.expenseAmount?.confidence >= confidenceThreshold
        ) {
            expenseVals.amount = ocrData.expenseAmount?.amount;

            if (currencies.some((c) => c.value === ocrData.expenseAmount?.currencyCode)) {
                expenseVals.currency = ocrData.expenseAmount?.currencyCode;
                usedCurrency = true;
            } else {
                expenseVals.currency = 'USD'; // Default to USD on unsupported currency code
                usedCurrency = false;
            }

            usedAmount = true;

            const ocrVal = {
                scanId: ocrData.scanId,
                amount: ocrData.expenseAmount?.amount,
                currency: ocrData.expenseAmount?.currencyCode,
            };

            setUsedOcrValues((ocrUsedValues) => [...ocrUsedValues, ocrVal]);
        }

        const ocrUsageLogForScan = {
            expenseId: -1,
            scanId: ocrData.scanId,
            fieldUsage: {
                transactionDate: usedTransactionDate,
                merchantName: usedMerchantName,
                amount: usedAmount,
                currency: usedCurrency,
            },
        };

        setOcrUsageLogs((ocrUsageLogs) => [...ocrUsageLogs, ocrUsageLogForScan]);
        setValue(`expenseEntries[${activeTabIndex}]`, expenseVals);
    };

    const validateSubmit = async () => {
        clearAllHiddenErrors();

        const allFieldsOk = await trigger(undefined, { shouldFocus: true });

        const postingOk = validateAllPostingDatesOnSubmit(
            getValues('expenseEntries'),
            setError,
            clearErrors
        );

        if (!allFieldsOk || !postingOk) return;

        handleSubmit(onSubmit)();
    };

    const validateSave = async () => {
        clearAllHiddenErrors();

        const allFieldsOk = await trigger(undefined, { shouldFocus: true });

        const postingOk = validateAllPostingDatesOnSubmit(
            getValues('expenseEntries'),
            setError,
            clearErrors
        );

        if (!allFieldsOk || !postingOk) return;

        if (errors.balance) {
            setShowBalanceError(true);
            return;
        }
        clearErrors('balance');
        setShowBalanceError(false);

        handleSubmit(onSave)();
    };

    const onSubmit = async (data) => {
        try {
            setIsLoading(true);
            const newExpense = createExpenseFromExpenseEntries(data.expenseEntries);
            newExpense.ocrFieldUsage = getOcrUsage(newExpense);
            const expenseResponse = await createExpense(newExpense);
            const fileUploads = getValues(`fileUploads`);
            await uploadImages(
                fileUploads,
                expenseResponse,
                ExpenseModeEnum.ClinicianReimbursement
            );

            history.push({
                pathname: '/expenses/submit',
                state: { prevPage: '/expenses/clinician-reimbursement' },
            });
        } catch (error) {
            console.error('Error onSubmit Clinician Reimbursement Expense Entry');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const onSave = async (data) => {
        try {
            setIsLoading(true);
            let entries = data.expenseEntries;
            if (isCorrection) {
                entries = createCorrectionExpenseFromEntries(entries);
            }
            let updatedExpense = createExpenseFromExpenseEntries(entries);
            updatedExpense.ocrFieldUsage = getOcrUsage(updatedExpense);
            const expenseResponse = await updateExpense(updatedExpense.id, updatedExpense);
            const fileUploads = getValues(`fileUploads`);
            if (!isCorrection) {
                await uploadImages(
                    fileUploads,
                    expenseResponse,
                    ExpenseModeEnum.ClinicianReimbursement
                );
            }

            history.push({
                pathname: '/expenses/submit',
                state: { prevPage: '/expenses/review' },
            });
        } catch (error) {
            console.error('Error onSave Clinician Reimbursement Expense Entry');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getOcrUsage = (expense) => {
        const preparedOcrUsageLogs = [];

        ocrUsageLogs.forEach((usageLog) => {
            const scanUsedValues = ocrUsedValues.filter(
                (usedValue) => usedValue.scanId === usageLog.scanId
            );

            if (
                usageLog.fieldUsage.transactionDate &&
                scanUsedValues.find((val) => !!val.transactionDate).transactionDate !==
                    expense.transactionDate
            ) {
                usageLog.fieldUsage.transactionDate = false;
            }

            if (
                usageLog.fieldUsage.merchantName &&
                scanUsedValues.find((val) => !!val.merchantName).merchantName !==
                    expense.merchantName
            ) {
                usageLog.fieldUsage.merchantName = false;
            }

            if (
                usageLog.fieldUsage.amount &&
                scanUsedValues.find((val) => !!val.amount).amount !== expense.amount
            ) {
                usageLog.fieldUsage.amount = false;
            }

            if (
                usageLog.fieldUsage.currency &&
                scanUsedValues.find((val) => !!val.currency).currency !== expense.currency
            ) {
                usageLog.fieldUsage.currency = false;
            }

            preparedOcrUsageLogs.push({
                ...usageLog,
                fieldUsage: JSON.stringify(usageLog.fieldUsage),
            });
        });
        return preparedOcrUsageLogs;
    };

    const handleAddExpense = () => {
        const newExpense = {
            ...getValues('expenseEntries[0]'),
            label: 'New Expense',
            reimbursedAmount: '',
            billType: '',
            id: null,
            created: null,
            lastModified: null,
            status: ExpenseStatusValuesEnum.New,
            source: 'LtAssociate', // This will Ensure any Split from a source: 'Clinician' will be change to 'LtAssociate'
            highPriority: false,
            resubmittedDate: null,
            submittedDate: null,
            mobileSubmission: false,
            salespersonName: '',
            recruiterName: '',
            description: '',
        };

        const newFileUpload = {
            ...getValues('fileUploads[0]'),
        };

        if (newFileUpload) {
            if (newFileUpload.existingFiles && newFileUpload.existingFiles.length > 0) {
                newFileUpload.existingFiles = newFileUpload.existingFiles
                    .filter((file) => !file.isCombinedPdf)
                    .map((file) => ({
                        ...file,
                        expenseId: null, // Clear out the Existing ExpenseId
                    }));
            }

            const cloneNewFiles = [];
            if (newFileUpload.newFiles && newFileUpload.newFiles.length > 0) {
                newFileUpload.newFiles.forEach((file) => {
                    const cloneNewFile = new File([file], file.name, { type: file.type });
                    // Regenerate the preview to make unique for the tab
                    Object.assign(cloneNewFile, {
                        preview: URL.createObjectURL(file),
                    });
                    cloneNewFiles.push(cloneNewFile);
                });
            }
            newFileUpload.newFiles = cloneNewFiles;

            const fileUploads = getValues(`fileUploads`);
            fileUploads.push(newFileUpload);
            setValue(`fileUploads`, fileUploads);
        }

        if (newExpense.hasOwnProperty('errors')) {
            delete newExpense.errors;
        }
        if (newExpense.hasOwnProperty('statusHistory')) {
            delete newExpense.statusHistory;
        }
        if (newExpense.hasOwnProperty('relatedErrors')) {
            delete newExpense.relatedErrors;
        }
        if (newExpense.hasOwnProperty('importedData')) {
            delete newExpense.importedData;
        }
        if (!newExpense.splitTransactionComment) {
            newExpense.splitTransactionComment = '';
        }
        append(newExpense);
        setActiveTabIndex(expenseEntries.length);
    };

    const resetImages = (index) => {
        // Resort/Reset Images
        const fileUploads = [...getValues('fileUploads')];
        fileUploads.splice(index, 1);
        setValue('fileUploads', fileUploads, { shouldDirty: true, shouldValidate: false });
    };

    const handleRemoveExpense = (index) => {
        // This was added to address a rendering issue
        // It turns off the TabPanel Display removes the item and then turns back on the display
        setActiveTabIndex(index);
        setRemoveTabs(true);
    };

    const enableCorrectionEditMode = () => {
        setOpenConfirmationEditModal(false);
        setEnableEditByModal(true);
        const currentVersion = getValues(`expenseEntries.${activeTabIndex}.expenseVersion`);
        const updatedVersion = currentVersion ? currentVersion + 1 : 1;
        setIsCorrection(true);
        setValue(`expenseEntries.${activeTabIndex}.expenseVersion`, updatedVersion);
    };

    return (
        <Container maxWidth="xl" className={classes.ContainerRoot}>
            <FormProvider {...methods}>
                <Grid container direction="column" spacing={2}>
                    <ExpenseEntryHeader
                        title={title}
                        backLink={backLink}
                        expensesPath={expensesPath}
                    />
                    {isLoading ? (
                        <CircularProgress color="primary" />
                    ) : (
                        <>
                            <Grid item xs={12}>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <Grid container spacing={4}>
                                        <Grid item sm={7} md={8} lg={9} xs={12}>
                                            <ExpenseTabs value={activeTabIndex}>
                                                {watchExpenseEntries.map((expense, index) => (
                                                    <ExpenseTab
                                                        key={index}
                                                        id={`expense-tab-${index}`}
                                                        label={
                                                            <ExpenseTabLabel
                                                                isActive={activeTabIndex === index}
                                                                index={index}
                                                                handleRemoveTab={() =>
                                                                    handleRemoveExpense(index)
                                                                }
                                                                setValue={setActiveTabIndex}
                                                                tab={expense}
                                                                mode={
                                                                    ExpenseModeEnum.ClinicianReimbursement
                                                                }
                                                                isViewOnly={isViewOnly}
                                                                originalPrimaryExpense={
                                                                    originalPrimaryExpense
                                                                }
                                                                isAnyExpensePosted={
                                                                    isAnyExpensePosted
                                                                }
                                                            />
                                                        }
                                                    />
                                                ))}
                                                {!isViewOnly &&
                                                    !(
                                                        isAnyExpensePosted ||
                                                        (originalPrimaryExpense &&
                                                            originalPrimaryExpense.status ===
                                                                ExpenseStatusValuesEnum.PendingSubmission)
                                                    ) &&
                                                    !(
                                                        watchExpenseEntries[0].status ===
                                                            ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                        originalPrimaryExpense?.status ===
                                                            ExpenseStatusValuesEnum.NeedMoreInformation
                                                    ) && (
                                                        <AddExpenseTab
                                                            label={<AddIcon color="primary" />}
                                                            disabled={
                                                                watchExpenseEntries[0].status ===
                                                                    ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                                originalPrimaryExpense?.status ===
                                                                    ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                                watchExpenseEntries.length === 5 ||
                                                                isExpenseViewOnlyByStatus(
                                                                    originalExpenseEntries[
                                                                        activeTabIndex
                                                                    ]
                                                                )
                                                            }
                                                            onClick={handleAddExpense}
                                                            id="expense-tab-add"
                                                        />
                                                    )}
                                            </ExpenseTabs>
                                            {!removeTabs && (
                                                <div>
                                                    {watchExpenseEntries.map((expense, index) => (
                                                        <ExpenseTabPanel
                                                            className={classes.tabPanel}
                                                            key={`${index}-panel`}
                                                            value={activeTabIndex}
                                                            index={index}
                                                            id={`expense-panel-${index}`}
                                                        >
                                                            <Box
                                                                className={
                                                                    classes.leftIconContainer
                                                                }
                                                            >
                                                                {enableEditButtonByFF &&
                                                                    isExpenseViewOnlyByStatus(
                                                                        originalExpenseEntries[
                                                                            index
                                                                        ]
                                                                    ) &&
                                                                    !enableEditByModal && (
                                                                        <Button
                                                                            variant="contained"
                                                                            className={
                                                                                classes.editButton
                                                                            }
                                                                            color="primary"
                                                                            onClick={() =>
                                                                                setOpenConfirmationEditModal(
                                                                                    true
                                                                                )
                                                                            }
                                                                        >
                                                                            Edit
                                                                        </Button>
                                                                    )}
                                                            </Box>
                                                            <Box className={classes.iconContainer}>
                                                                {assignmentsByIndex[index] && (
                                                                    <Button
                                                                        variant="outlined"
                                                                        color="primary"
                                                                        href={
                                                                            assignmentsByIndex[
                                                                                index
                                                                            ].sfLink
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        endIcon={<OpenInNew />}
                                                                        className={
                                                                            classes.viewBookingButton
                                                                        }
                                                                    >
                                                                        View booking
                                                                    </Button>
                                                                )}
                                                                <Controller
                                                                    name={`expenseEntries[${index}].highPriority`}
                                                                    control={control}
                                                                    defaultValue={
                                                                        expense.highPriority
                                                                    }
                                                                    render={({
                                                                        onChange,
                                                                        value,
                                                                    }) => (
                                                                        <FlagIcon
                                                                            fontSize="large"
                                                                            className={
                                                                                classes.flagIcon
                                                                            }
                                                                            onClick={() =>
                                                                                onChange(!value)
                                                                            }
                                                                            style={{
                                                                                color: value
                                                                                    ? COLORS.LT_PEPPER
                                                                                    : COLORS.LT_MIDNIGHT25,
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </Box>
                                                            <div className={classes.alertContainer}>
                                                                {expense.errors &&
                                                                    expense.errors.length > 0 && (
                                                                        <ExpenseNeedsReviewAlert
                                                                            expense={expense}
                                                                        />
                                                                    )}
                                                            </div>

                                                            <Grid
                                                                container
                                                                direction="column"
                                                                className={
                                                                    classes.formGridContainer
                                                                }
                                                            >
                                                                <Grid item xs={12}>
                                                                    <BookingDetailsForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        divisions={divisions}
                                                                        isSGA={[]}
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ]
                                                                            ) &&
                                                                                !enableEditByModal)
                                                                        }
                                                                        onAssignmentChange={
                                                                            handleAssignmentChange
                                                                        }
                                                                        enableEditByCorrection={
                                                                            enableEditByCorrection
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <ExpenseReceiptUploadForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        mode={
                                                                            ExpenseModeEnum.ClinicianReimbursement
                                                                        }
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ]
                                                                            ) &&
                                                                                !enableEditByModal &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                        onOCRUpload={
                                                                            autoFillWithOCR
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <ClinicianExpenseDetailsForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        expenseCategories={
                                                                            expenseCategories
                                                                        }
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ]
                                                                            ) &&
                                                                                !enableEditByModal &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                        enablePostDate={
                                                                            enablePostDate
                                                                        }
                                                                        enableEditByCorrection={
                                                                            enableEditByCorrection
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <InternalNotesForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        statusesAllowed={
                                                                            statusesAllowed
                                                                        }
                                                                        isReimbursement
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ]
                                                                            ) &&
                                                                                !enableEditByModal &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                        numTabs={
                                                                            watchExpenseEntries.length
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <ReasonForSplittingForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        isReimbursement
                                                                        isViewOnly={
                                                                            isViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ]
                                                                            ) &&
                                                                                !enableEditByModal &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                        mode={
                                                                            ExpenseModeEnum.ClinicianReimbursement
                                                                        }
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </ExpenseTabPanel>
                                                    ))}
                                                </div>
                                            )}
                                        </Grid>
                                        <Grid item md={4} sm={5} lg={3} xs={12}>
                                            <div className={classes.summary}>
                                                <ExpenseSummary
                                                    setError={setError}
                                                    clearErrors={clearErrors}
                                                    setShowBalanceError={setShowBalanceError}
                                                    showBalanceError={showBalanceError}
                                                    setShowBalanceWarning={setShowBalanceWarning}
                                                    isAnyApproved={isAnyApproved}
                                                    isReimbursement
                                                />
                                                {showBalanceError && (
                                                    <Typography color="error">
                                                        {errors.balance.message}
                                                    </Typography>
                                                )}
                                                {showBalanceWarning && (
                                                    <Typography
                                                        className={classes.warningMessage}
                                                        color="inherit"
                                                    >
                                                        Warning: The reimbursed amount exceeds the
                                                        submitted amount. Please review the values
                                                        before saving or submitting.
                                                    </Typography>
                                                )}
                                            </div>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Grid>
                            {hasErrors > 0 && (
                                <Grid container className={classes.alertContainer}>
                                    <Grid item sm={7} md={8} lg={9} xs={12}>
                                        <MuiAlert severity="error">
                                            Please review the highlighted fields above and correct
                                            the errors to submit your expense.
                                        </MuiAlert>
                                    </Grid>
                                </Grid>
                            )}
                            {!(
                                watchExpenseEntries.length === 1 &&
                                watchExpenseEntries[0].status ===
                                    ExpenseStatusValuesEnum.PendingSubmission
                            ) &&
                                !isViewOnly && (
                                    <Grid item xs={12}>
                                        <ExpenseButtons
                                            validateSubmit={validateSubmit}
                                            validateSave={validateSave}
                                            expensesPath={id ? '/expenses/review' : '/expenses'}
                                            enableEditByModal={enableEditByModal}
                                        />
                                    </Grid>
                                )}
                        </>
                    )}
                </Grid>
            </FormProvider>
            <Alert alert={alert} setAlert={setAlert} />
            <ConfirmationModal
                open={openConfirmationEditModal}
                text="You are attempting to edit an approved expense, do you want to continue?"
                onYes={enableCorrectionEditMode}
                onNo={() => {
                    setOpenConfirmationEditModal(false);
                    setEnableEditByModal(false);
                }}
            />
        </Container>
    );
};

export default withStyles(styles)(ClinicianReimbursement);
